import {
  createRouter,
  createWebHistory,
  LocationQuery,
  RouteRecordRaw,
} from 'vue-router';
import { DateWithZone } from '@/models/DateWithZone';
import { JST_URL_ROUTES } from '@/config/jstUrlRoutes';

// prettier-ignore
const routes: Array<RouteRecordRaw> = [
  { path: '/', component: () => import('@/views/containers/dashboard.vue') },
  { path: '/403', component: () => import('@/views/containers/access_denied.vue') },
  // auth
  {
    path: '/auth/setup_mfa',
    component: () => import('@/views/containers/auth/setup_mfa.vue'),
  },
  {
    path: '/auth/sign_in',
    component: () => import('@/views/containers/auth/sign_in.vue'),
  },
  {
    path: '/auth/sign_out',
    component: () => import('@/views/containers/auth/sign_out.vue'),
  },
  // dashboard
  {
    path: '/dashboard',
    component: () => import('@/views/containers/dashboard.vue'),
  },
  {
    path: '/aws/ec2/instance_state',
    component: () => import('@/views/containers/aws/ec2/instance_state.vue'),
  },
  {
    path: '/aws/ec2/spot_instance_interruption',
    component: () =>
      import('@/views/containers/aws/ec2/spot_instance_interruption.vue'),
  },
  {
    path: '/aws/ecs',
    component: () => import('@/views/containers/aws/ecs.vue'),
  },
  {
    path: '/aws/health',
    component: () => import('@/views/containers/aws/health.vue'),
  },
  {
    path: '/aws/guardduty',
    component: () => import('@/views/containers/aws/guardduty.vue'),
  },
  {
    path: '/aws/inspector/ec2_network',
    component: () =>
      import('@/views/containers/aws/inspector/ec2NetworkEvent.vue'),
  },
  {
    path: '/aws/inspector/ec2_package',
    component: () =>
      import('@/views/containers/aws/inspector/ec2PackageEvent.vue'),
  },
  {
    path: '/aws/inspector/ecrImage',
    component: () => import('@/views/containers/aws/inspector/ecrImage.vue'),
  },
  {
    path: '/aws/cloudwatch',
    component: () => import('@/views/containers/aws/cloudwatch.vue'),
  },
  {
    path: '/aws/rds',
    component: () => import('@/views/containers/aws/rds.vue'),
  },
  {
    path: '/aws/securityhub',
    component: () => import('@/views/containers/aws/securityhub.vue'),
  },
  { path: '/sentry', component: () => import('@/views/containers/sentry.vue') },
  {
    path: '/datadog',
    component: () => import('@/views/containers/datadog.vue'),
  },
  {
    path: '/pagerduty',
    component: () => import('@/views/containers/pagerduty.vue'),
  },
  {
    path: '/newrelic',
    component: () => import('@/views/containers/newrelic.vue'),
  },
  // { path: '/oncall', component: () =>  import('@/views/containers/oncall.vue') }, // 一旦除外
  // JST 形式が必要なルートは src/config/jstUrlRoutes.ts に記載すること
  {
    path: '/costs',
    component: () => import('@/views/containers/costs.vue'),
  },
  {
    path: '/allocation/settings',
    component: () => import('@/views/containers/cost_allocation.vue'),
  },
  {
    path: '/allocation/settings/edit',
    component: () => import('@/views/containers/cost_allocation_edit.vue'),
  },
  {
    path: '/allocation/retroactive',
    component: () => import('@/views/containers/retroactive.vue'),
  },
  {
    path: '/allocation',
    component: () => import('@/views/containers/cost_visualization.vue'),
  },
  {
    path: '/allocation/detail',
    component: () => import('@/views/containers/cost_allocation_detail.vue'),
  },
  {
    path: '/allocation/target_transition',
    component: () => import('@/views/containers/cost_target_transition.vue'),
  },
  // insights
  {
    path: '/insights',
    component: () => import('@/views/containers/insights.vue'),
  },
  {
    path: '/insights/budgets/settings',
    component: () => import('@/views/containers/budgets.vue'),
  },
  {
    path: '/cost_reports',
    component: () => import('@/views/containers/cost_reports.vue'),
  },
  {
    path: '/ai_assistant',
    component: () => import('@/views/containers/ai_assistant.vue'),
  },
  {
    path: '/optimization',
    component: () => import('@/views/containers/optimization.vue'),
  },
  {
    path: '/optimization/:label',
    component: () => import('@/views/containers/optimization_detail.vue'),
  },
  // settings
  {
    path: '/settings/organizations/products',
    component: () =>
      import('@/views/containers/settings/organizations/products.vue'),
  },
  {
    path: '/settings/organizations/ai_assistant',
    component: () =>
      import('@/views/containers/settings/organizations/ai_assistant.vue'),
  },
  {
    path: '/settings/organizations/users',
    component: () =>
      import('@/views/containers/settings/organizations/users.vue'),
  },
  {
    path: '/settings/organizations/notifications/channels',
    component: () =>
      import('@/views/containers/settings/organizations/notifications/channels.vue'),
  },
  {
    path: '/settings/organizations/accesscontrol/roles',
    component: () =>
      import('@/views/containers/settings/organizations/accesscontrol/roles.vue'),
  },
  {
    path: '/settings/organizations/accesscontrol/views',
    component: () =>
      import('@/views/containers/settings/organizations/accesscontrol/views.vue'),
  },
  {
    path: '/settings/tenants/custom_prompt',
    component: () =>
      import('@/views/containers/settings/tenants/custom_prompt.vue'),
  },
  {
    path: '/settings/products/integrations',
    component: () =>
      import('@/views/containers/settings/products/integrations.vue'),
  },
  {
    path: '/settings/personal/profile',
    component: () => import('@/views/containers/settings/personal/profile.vue'),
  },
  {
    path: '/settings/personal/timezone',
    component: () =>
      import('@/views/containers/settings/personal/timezone.vue'),
  },
  {
    path: '/settings/products/api_keys',
    component: () =>
      import('@/views/containers/settings/products/api_keys.vue'),
  },
  {
    path: '/settings/products/notifications/channels',
    component: () =>
      import('@/views/containers/settings/products/notifications/channels.vue'),
  },
  {
    path: '/settings/products/notifications/settings',
    component: () =>
      import('@/views/containers/settings/products/notifications/settings.vue'),
  },
  {
    path: '/settings/verification_email',
    component: () =>
      import('@/views/containers/settings/verification_email.vue'),
  },
  {
    path: '/tasks',
    component: () => import('@/views/containers/tasks/index.vue'),
  },
  {
    path: '/documents',
    component: () => import('@/views/containers/documents.vue'),
  },
  {
    path: '/:catchAll(.*)',
    component: () => import('@/views/containers/not_found.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

// JST_URL_ROUTES のルートに UTC Z 形式の日時パラメータで流入した場合、
// user TZ 形式に書き換えてから遷移させる。
// 外部 URL / 古い bookmark 由来の UTC Z を cost flow メインルート着地時に補正して、
// 以後の URL / API 呼び出しを一貫して JST 形式に保つ。
const UTC_Z_PATTERN = /Z$/;
const normalizeDateParamsToJst = (query: LocationQuery): LocationQuery => {
  let changed = false;
  const result: LocationQuery = { ...query };
  for (const key of ['start_time', 'end_time']) {
    const value = query[key];
    if (typeof value !== 'string') continue;
    if (!UTC_Z_PATTERN.test(value)) continue;
    const converted = new DateWithZone(value).toISOString();
    if (converted && converted !== value) {
      result[key] = converted;
      changed = true;
    }
  }
  return changed ? result : query;
};

router.beforeEach((to, from, next) => {
  if (!JST_URL_ROUTES.includes(to.path)) return next();
  const normalized = normalizeDateParamsToJst(to.query);
  if (normalized === to.query) return next();
  next({
    path: to.path,
    query: normalized,
    hash: to.hash,
    replace: true,
  });
});

// デプロイ後のチャンク失効による動的インポート失敗は、main.ts の vite:preloadError
// ハンドラ（クールダウン付きリロード）で一元的に扱う。message ベースの router.onError は
// 生の error Event 経路を拾えず、かつ二重リロード/ループの原因になるため置き換えた。

export default router;
